<template>
  <div class="modern-sales-detail pa-6 bg-light-grey rounded-xl overflow-hidden">
    <!-- Header Section for Approving state -->
    <v-row v-if="approbing" justify="center" class="mb-6">
      <div class="d-flex align-center bg-success lighten-5 pa-4 rounded-lg border-success">
        <v-icon color="success" class="mr-3">fas fa-handshake</v-icon>
        <div class="text-h5 font-weight-black success--text tracking-tighter uppercase">Confirmación de Negocio</div>
      </div>
    </v-row>

    <!-- Main Header -->
    <v-row class="mb-8 align-center">
      <v-col cols="12" md="8">
        <div class="d-flex align-center flex-wrap gap-3">
          <v-avatar color="primary" size="44" class="shadow-sm border-white-sm mr-2">
            <v-icon color="white" small>fas fa-file-invoice-dollar</v-icon>
          </v-avatar>
          <div>
            <div class="d-flex align-baseline gap-2">
              <h1 class="text-h4 font-weight-black primary--text lh-1">Cotización #{{ salesNote.id }}</h1>
              <v-chip
                v-if="salesNote.status === 'rejected'"
                color="error lighten-4"
                text-color="error darken-2"
                small
                label
                class="font-weight-black tracking-widest ml-2 elevation-0"
              >
                <v-icon x-small left>fas fa-times-circle</v-icon>
                Rechazada
              </v-chip>
              <v-chip
                v-else-if="salesNote.work_order || salesNote.status === 'confirmed'"
                color="success lighten-4"
                text-color="success darken-2"
                small
                label
                class="font-weight-black tracking-widest ml-2 elevation-0 border-success"
              >
                <v-icon x-small left>fas fa-check-circle</v-icon>
                Confirmada
              </v-chip>
              <v-chip
                v-else-if="salesNote.status === 'approved'"
                color="teal lighten-4"
                text-color="teal darken-3"
                small
                label
                class="font-weight-black tracking-widest ml-2 elevation-0"
              >
                <v-icon x-small left>fas fa-stamp</v-icon>
                Aprobada
              </v-chip>
              <v-chip
                v-else
                color="orange lighten-4"
                text-color="orange darken-3"
                small
                label
                class="font-weight-black tracking-widest ml-2 elevation-0"
              >
                <v-icon x-small left>fas fa-clock</v-icon>
                Pendiente
              </v-chip>
            </div>
            <div class="caption grey--text font-weight-bold mt-1 d-flex align-center flex-wrap gap-x-4">
              <span class="d-flex align-center"><v-icon x-small class="mr-1" color="grey lighten-1">fas fa-user-plus</v-icon> {{ salesNote.created_by_full_name }} — {{ salesNote.created_at | moment('DD/MM/YYYY') }}</span>
              <span v-if="salesNote.updated_by" class="d-flex align-center"><v-icon x-small class="mr-1" color="grey lighten-1">fas fa-pen-nib</v-icon> {{ salesNote.updated_by_full_name }}</span>
            </div>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="4" class="text-right">
        <div class="d-flex justify-end align-center gap-2">
          <v-menu v-if="!approbing" offset-y left transition="slide-y-transition" rounded="lg">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon color="primary" v-bind="attrs" v-on="on" class="grey lighten-4 shadow-sm h-40 w-40">
                <v-icon small>fas fa-ellipsis-v</v-icon>
              </v-btn>
            </template>
            <v-list dense class="py-1 bespoke-menu">
              <v-list-item @click="edit" class="px-4">
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="primary">fas fa-pen</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium">Modificar</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="$can('readCost', 'SalesNote')" @click="generateCostReport" class="px-4">
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="primary">fas fa-calculator</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium">Informe de Costos</v-list-item-title>
              </v-list-item>
              <v-list-item :disabled="!canEmitPdf" @click="viewQuotation" class="px-4">
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="blue-grey">fas fa-file-pdf</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium">Ver PDF Cliente</v-list-item-title>
              </v-list-item>
              <v-list-item :disabled="!canEmitPdf" @click="generateQuotation" class="px-4">
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="blue-grey">fas fa-download</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium">Descargar PDF Cliente</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="$can('duplicate', 'SalesNote')" @click="duplicate" class="px-4">
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="primary">fas fa-clone</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium">Duplicar</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn icon @click="$emit('close')" class="grey lighten-4 shadow-sm h-40 w-40">
            <v-icon small>fas fa-times</v-icon>
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Mini KPI Section -->
    <v-row dense class="mb-8" v-if="totals">
      <v-col cols="12" sm="3">
        <v-card class="bespoke-mini-card flat-border text-center pa-3 h-100" flat outlined>
          <div class="metric-label">Superficie</div>
          <div class="text-h6 font-weight-black primary--text tracking-tighter lh-1">{{ (totals.mts2 || 0) | number('0,0.00') }} m²</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="3" v-if="$can('readCost', 'SalesNote')">
        <v-card class="bespoke-mini-card flat-border text-center pa-3 h-100" flat outlined>
          <div class="metric-label">Costo Total</div>
          <div class="text-h6 font-weight-black purple--text tracking-tighter lh-1">{{ (totals.cost || 0) | moneda }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="3">
        <v-card class="bespoke-mini-card flat-border text-center pa-3 h-100 primary lighten-5 border-primary-light" flat outlined>
          <div class="metric-label primary--text">Total Neto</div>
          <div class="text-h5 font-weight-black primary--text tracking-tighter lh-1">{{ (totals.total || 0) | moneda }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="3" v-if="$can('readCost', 'SalesNote')">
        <v-card class="bespoke-mini-card flat-border text-center pa-3 h-100" :class="(totals.margin || 0) > 0.3 ? 'success lighten-5' : 'warning lighten-5'" flat outlined>
          <div class="metric-label" :class="(totals.margin || 0) > 0.3 ? 'success--text' : 'warning--text'">Margen</div>
          <div class="text-h6 font-weight-black tracking-tighter lh-1" :class="(totals.margin || 0) > 0.3 ? 'success--text' : 'warning--text text--darken-2'">
            {{ (totals.margin || 0) | percent(1) }}
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Content Grid -->
    <v-row class="mb-8">
      <v-col cols="12" md="6">
        <v-card outlined class="modern-card h-100 overflow-hidden shadow-xs">
          <v-sheet color="grey lighten-4" class="px-4 py-2 border-bottom d-flex align-center">
            <v-icon x-small color="primary" class="mr-2">fas fa-building</v-icon>
            <span class="caption font-weight-black grey--text text--darken-2 uppercase tracking-widest">Información del Proyecto</span>
          </v-sheet>
          <v-card-text class="pa-4">
            <v-row dense>
              <v-col cols="12" class="mb-3">
                <div class="caption grey--text uppercase font-weight-bold tracking-widest mb-1">Cliente</div>
                <div class="body-2 font-weight-black primary--text">{{ (salesNote.sales_note_customer && salesNote.sales_note_customer.name) || '—' }}</div>
              </v-col>
              <v-col cols="12" class="mb-3">
                <div class="caption grey--text uppercase font-weight-bold tracking-widest mb-1">Nombre del Proyecto</div>
                <div class="body-2 font-weight-medium grey--text text--darken-4">{{ salesNote.name }}</div>
              </v-col>
              <v-col cols="6">
                <div class="caption grey--text uppercase font-weight-bold tracking-widest mb-1">Probable Cierre</div>
                <div class="body-2 font-weight-black orange--text">{{ salesNote.closing_date | moment('DD/MM/YYYY') }}</div>
              </v-col>
              <v-col cols="6" v-if="salesNote.work_order">
                <div class="caption grey--text uppercase font-weight-bold tracking-widest mb-1">OT Asociada</div>
                <div class="body-2 font-weight-black success--text">#{{ salesNote.work_order }}</div>
              </v-col>
              <v-col cols="12" v-if="salesNote.status === 'rejected'">
                <div class="caption grey--text uppercase font-weight-bold tracking-widest mb-1">Motivo de rechazo</div>
                <div class="body-2 font-weight-medium error--text">
                  {{ salesNote.rejected_reason === 'price' ? 'Precio' : (salesNote.rejected_reason === 'delivery_date' ? 'Fecha de entrega' : 'Rechazada') }}
                  <span v-if="salesNote.rejected_at"> — {{ salesNote.rejected_at | moment('DD/MM/YYYY HH:mm') }}</span>
                </div>
                <div v-if="salesNote.rejected_note" class="caption font-italic grey--text mt-1">{{ salesNote.rejected_note }}</div>
              </v-col>
              <v-col cols="12" v-else-if="salesNote.status === 'approved' && salesNote.approved_by_full_name">
                <div class="caption grey--text uppercase font-weight-bold tracking-widest mb-1">Aprobación interna</div>
                <div class="body-2 font-weight-medium grey--text text--darken-4">
                  {{ salesNote.approved_by_full_name }}
                  <span v-if="salesNote.approved_at"> — {{ salesNote.approved_at | moment('DD/MM/YYYY HH:mm') }}</span>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card outlined class="modern-card h-100 overflow-hidden shadow-xs">
          <v-sheet color="grey lighten-4" class="px-4 py-2 border-bottom d-flex align-center">
            <v-icon x-small color="primary" class="mr-2">fas fa-user-tie</v-icon>
            <span class="caption font-weight-black grey--text text--darken-2 uppercase tracking-widest">Responsables</span>
          </v-sheet>
          <v-card-text class="pa-4">
            <v-row dense>
              <v-col cols="12" class="mb-3">
                <div class="caption grey--text uppercase font-weight-bold tracking-widest mb-1">Contacto del Cliente</div>
                <div v-if="salesNote.sales_note_contact" class="body-2 font-weight-black grey--text text--darken-4 d-flex align-center">
                   <v-avatar size="20" color="blue lighten-5" class="mr-2"><span class="caption font-weight-black blue--text" style="font-size: 10px !important;">{{ contactInitial }}</span></v-avatar>
                   {{ salesNote.sales_note_contact.first_name }} {{ salesNote.sales_note_contact.last_name }}
                </div>
                <div v-else class="caption font-italic grey--text">Sin contacto asignado</div>
              </v-col>
              <v-col cols="12" class="mb-3">
                <div class="caption grey--text uppercase font-weight-bold tracking-widest mb-1">Vendedor / Ejecutivo</div>
                <div v-if="seller" class="body-2 font-weight-medium grey--text text--darken-4 d-flex align-center">
                   <v-avatar size="20" color="primary lighten-4" class="mr-2"><span class="caption font-weight-black primary--text" style="font-size: 10px !important;">{{ sellerInitial }}</span></v-avatar>
                   {{ seller.first_name }} {{ seller.last_name }}
                </div>
                <div v-else class="caption font-italic grey--text">Sin vendedor asignado</div>
              </v-col>
              <v-col cols="12" v-if="$store.getters.getCompany && $store.getters.getCompany.distribution_list_enabled">
                <distribution-list :sales_note="salesNote" />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Observations -->
    <v-card flat outlined class="modern-card pa-4 mb-8 bg-white shadow-xs" v-if="salesNote.notes">
       <div class="caption grey--text uppercase font-weight-black tracking-widest mb-2 border-bottom pb-2">Observaciones de la Cotización</div>
       <div class="body-2 font-italic grey--text text--darken-3">"{{ salesNote.notes }}"</div>
    </v-card>

    <!-- Data Sections -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-tabs v-model="tab" background-color="grey lighten-5" color="primary" grow class="border-bottom">
        <v-tab class="text-none font-weight-black py-4">
          <v-icon left x-small>fas fa-boxes</v-icon> Productos
        </v-tab>
        <v-tab v-if="salesNote.details && salesNote.details.costs && salesNote.details.costs.length > 0" class="text-none font-weight-black py-4">
          <v-icon left x-small>fas fa-plus-circle</v-icon> Adicionales
        </v-tab>
        <v-tab v-if="salesNote.files && salesNote.files.length > 0" class="text-none font-weight-black py-4">
          <v-icon left x-small>fas fa-paperclip</v-icon> Archivos
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item class="bg-white">
          <v-data-table
            :headers="productsTableHeaders"
            :items="(salesNote.details && salesNote.details.products) || []"
            :single-expand="true"
            :expanded.sync="expanded"
            item-key="key"
            show-expand
            class="modern-table elevation-0"
            hide-default-footer
            :items-per-page="-1"
          >
            <template v-slot:item.name="{ item }">
              <div class="py-3">
                <div class="body-2 font-weight-black primary--text">{{ item.name }}</div>
                <div v-if="item.sku" class="caption grey--text font-weight-bold">SKU: {{ item.sku }}</div>
              </div>
            </template>
            <template v-slot:item.qty="{ item }">
              <span class="body-2 font-weight-black">{{ item.qty }}</span>
            </template>
            <template v-slot:item.totalMts2="{ item }">
              <v-chip x-small label outlined color="grey darken-1" class="font-weight-black">{{ item.totalMts2 | number('0,0.00') }} m²</v-chip>
            </template>
            <template v-slot:item.unitCost="{ item }">
              <div class="text-right" v-if="$can('readCost', 'SalesNote')">
                <span class="caption font-weight-bold purple--text">{{ (item.totalCost / (item.sell_unit_m2 ? item.totalMts2 : item.qty)) | moneda }}</span>
              </div>
            </template>
            <template v-slot:item.totalCost="{ item }">
              <div class="text-right" v-if="$can('readCost', 'SalesNote')">
                <span class="body-2 font-weight-black purple--text">{{ item.totalCost | moneda }}</span>
              </div>
            </template>
            <template v-slot:item.price="{ item }">
              <div class="d-flex align-center justify-end">
                <v-tooltip top v-if="item.price_bellow_original || item.margin_bellow_original">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" x-small color="warning" class="mr-2">fas fa-exclamation-triangle</v-icon>
                  </template>
                  <span>Alerta: Precio o Margen bajo el original</span>
                </v-tooltip>
                <span class="caption font-weight-bold grey--text text--darken-3">{{ item.price | moneda }}</span>
                <v-icon x-small class="ml-2 color-grey-light">{{ item.sell_unit_m2 ? 'fas fa-vector-square' : 'fas fa-box' }}</v-icon>
              </div>
            </template>
            <template v-slot:item.totalPrice="{ item }">
              <div class="text-right">
                <div class="body-2 font-weight-black" :class="item.totalPrice > item.totalCost ? 'success--text' : 'error--text'">
                  {{ item.totalPrice | moneda }}
                </div>
              </div>
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="pa-0">
                <div class="pa-6 bg-grey-light border-left-primary">
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <recipe class="modern-card shadow-sm h-100" :mts2="item.totalMts2" :total="item.totalCost" :qty="item.qty" :items="item.recipe" :readonly="true" />
                    </v-col>
                    <v-col cols="12" md="6">
                      <supplies class="modern-card shadow-sm h-100" :mts2="item.totalMts2" :total="item.totalCost" :qty="item.qty" :items="item.product_supplies" :readonly="true" :product_width="item.width" :product_height="item.height" />
                    </v-col>
                  </v-row>
                  <div v-if="item.notes" class="mt-4 pa-4 bg-white border rounded-lg body-2 font-italic grey--text text--darken-3">
                    "{{ item.notes }}"
                  </div>
                </div>
              </td>
            </template>
          </v-data-table>
        </v-tab-item>

        <v-tab-item v-if="salesNote.details && salesNote.details.costs && salesNote.details.costs.length > 0" class="pa-4 bg-white">
          <v-data-table
            :headers="proyectCostsHeaders"
            :items="(salesNote.details && salesNote.details.costs) || []"
            class="modern-table elevation-0"
            hide-default-footer
            :items-per-page="-1"
          >
             <template v-slot:item.name="{ item }">
              <div class="d-flex align-center py-2">
                <v-avatar size="28" color="orange lighten-5" class="mr-2">
                  <v-icon x-small color="orange darken-2">fas fa-plus-circle</v-icon>
                </v-avatar>
                <span class="body-2 font-weight-black grey--text text--darken-4">{{ item.name }}</span>
              </div>
            </template>
            <template v-slot:item.unitCost="{ item }">
              <span class="caption font-weight-bold purple--text">{{ item.unitCost | moneda }}</span>
            </template>
            <template v-slot:item.totalCost="{ item }">
              <span class="body-2 font-weight-black purple--text">{{ item.totalCost | moneda }}</span>
            </template>
            <template v-slot:item.totalPrice="{ item }">
              <span class="body-2 font-weight-black" :class="item.totalPrice >= item.totalCost ? 'success--text' : 'error--text'">
                {{ item.totalPrice | moneda }}
              </span>
            </template>
          </v-data-table>
        </v-tab-item>

        <v-tab-item v-if="salesNote.files && salesNote.files.length > 0" class="pa-6 bg-white">
          <div class="d-flex flex-wrap gap-2">
            <v-chip v-for="file in salesNote.files" :key="file.url" label color="grey lighten-4" link :href="file.url" target="_blank" class="px-4 py-4 border shadow-xs">
              <v-icon x-small left color="primary">fas fa-file-pdf</v-icon>
              <span class="caption font-weight-black grey--text text--darken-3">{{ file.name }}</span>
            </v-chip>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import Recipe from '@/components/products/products/Recipe'
import DeliveryEstimation from '@/components/sales/DeliveryEstimation.vue'
import Supplies from '@/components/products/products/Supplies'
import DistributionList from '@/components/sales/DistributionList'
import { pdfQuotation, openQuotationPdf } from '@/doc_generators/quotation'
import notifications from '@/utils/notifications'
import { pdfCostReport } from '@/doc_generators/costReport'
import { canEmitQuotationPdf } from '@/utils/quotationApproval'

export default {
  name: 'SalesDetail',
  components: { Recipe, Supplies, DeliveryEstimation, DistributionList },
  props: {
    salesNote: { type: Object, required: true },
    approbing: { type: Boolean, default: false }
  },
  data() {
    return {
      tab: null,
      expanded: [],
      proyectCostsHeaders: [
        { text: 'Descripción Adicional', value: 'name', class: 'table-header-custom' },
        { text: 'Unidad', value: 'cost_unit.name', class: 'table-header-custom', align: 'center' },
        { text: 'Cant.', value: 'qty', class: 'table-header-custom', align: 'center', width: '80' },
        { text: 'Costo Unit.', value: 'unitCost', class: 'table-header-custom', align: 'end' },
        { text: 'Costo Total', value: 'totalCost', class: 'table-header-custom', align: 'end' },
        { text: 'Prec. Total', value: 'totalPrice', class: 'table-header-custom', align: 'end' },
      ]
    }
  },
  computed: {
    totals() {
      return (this.salesNote && this.salesNote.totals) || null
    },
    seller() {
      return (this.salesNote && this.salesNote.sales_note_customer && this.salesNote.sales_note_customer.seller) || null
    },
    contactInitial() {
      const contact = this.salesNote && this.salesNote.sales_note_contact
      if (contact && contact.first_name) {
        return contact.first_name.charAt(0).toUpperCase()
      }
      return '?'
    },
    sellerInitial() {
      if (this.seller && this.seller.first_name) {
        return this.seller.first_name.charAt(0).toUpperCase()
      }
      return '?'
    },
    canEmitPdf() {
      return canEmitQuotationPdf(this.$store, this.salesNote)
    },
    productsTableHeaders() {
      const header = [
        { text: 'Código', value: 'sku', class: 'table-header-custom', width: '100' },
        { text: 'Producto / Descripción', value: 'name', class: 'table-header-custom' },
        { text: 'Cant.', value: 'qty', class: 'table-header-custom', align: 'center', width: '80' },
        { text: 'Mts2', value: 'totalMts2', class: 'table-header-custom', align: 'end', width: '100' },
        { text: 'Cost. Unit.', value: 'unitCost', class: 'table-header-custom', align: 'end', available: this.$can('readCost', 'SalesNote') },
        { text: 'Cost. Total', value: 'totalCost', class: 'table-header-custom', align: 'end', available: this.$can('readCost', 'SalesNote') },
        { text: 'Prec. Unit.', value: 'price', class: 'table-header-custom', align: 'end' },
        { text: 'Total Neto', value: 'totalPrice', class: 'table-header-custom', align: 'end' },
        { text: '', value: 'data-table-expand', class: 'table-header-custom', width: '50' },
      ]
      return header.filter(item => item.available !== false)
    }
  },
  methods: {
    edit() {
      this.$router.push({ name: 'SalesNoteCreateUpdate', params: { id: this.salesNote.id } })
    },
    duplicate() {
      this.$router.push({ name: 'SalesNoteCreateUpdate', params: { id: this.salesNote.id, duplicate: true } })
    },
    generateQuotation() {
      if (!this.canEmitPdf) return
      pdfQuotation(this.salesNote)
    },
    viewQuotation() {
      if (!this.canEmitPdf) return
      openQuotationPdf(this.salesNote).catch(() => {
        notifications.showError('No se pudo abrir el PDF. Revise si el navegador bloqueó la ventana emergente.')
      })
    },
    generateCostReport() {
      pdfCostReport(this.salesNote)
    },
  },
}
</script>

<style scoped>
.modern-sales-detail { font-family: 'Outfit', sans-serif !important; }
.bg-light-grey { background-color: #f8fafc; }
.modern-card { border: 1px solid rgba(0,0,0,0.08) !important; border-radius: 12px !important; background: white; }
.shadow-xs { box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.03) !important; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.25; }

/* KPI Mini Cards */
.bespoke-mini-card { position: relative; background: white !important; border: 1px solid rgba(0,0,0,0.08) !important; border-radius: 12px !important; }
.metric-label { font-size: 0.65rem; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; }
.tracking-tighter { letter-spacing: -1px; }

/* Table Styling */
.table-header-custom { background-color: #f8fbfc !important; text-transform: uppercase; font-size: 0.65rem !important; font-weight: 800 !important; color: #64748b !important; letter-spacing: 1px !important; }
.modern-table >>> td { height: 60px !important; border-bottom: 1px solid rgba(0,0,0,0.03) !important; }
.modern-table >>> .v-data-table-header { border-bottom: 2px solid #e2e8f0 !important; }

.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
.gap-x-4 { column-gap: 16px; }

.bespoke-menu { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.1) !important; }
.h-40 { height: 40px !important; }
.w-40 { width: 40px !important; min-width: 40px !important; }

.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.06); }
.border-success { border: 1px solid rgba(var(--v-success-base), 0.1); }
.border-primary-light { border-color: rgba(var(--v-primary-base), 0.2) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-left-primary { border-left: 4px solid var(--v-primary-base) !important; }
.bg-grey-light { background-color: #f9fafb; }
.color-grey-light { color: #cbd5e1; }
.opacity-70 { opacity: 0.7; }
.opacity-80 { opacity: 0.8; }
.tracking-widest { letter-spacing: 1.5px; }
.uppercase { text-transform: uppercase; }
</style>